import tutorialScreenplay from './mock_data/tutorial_screenplay.json';
import tutorialCharacterBible from './mock_data/tutorial_character_bible.json';
import tutorialScenes from './mock_data/tutorial_scenes.json';

// ─── Exported tutorial content (used by NewProjectDialog typewriter) ──────────
export const TUTORIAL_PROJECT_TITLE = 'Max and the Treasure of Willow Park';
export const TUTORIAL_PROJECT_STORY = `Max, the goldendoodle, bounded ahead through the forest trail, his curly coat glinting in the sunlight. Emma laughed, trying to keep up, while Adam, her boyfriend, followed behind with a backpack full of snacks and water.

"Your dog's got more energy than both of us combined," Adam teased, adjusting his pack.

"He's not just my dog," Emma said proudly. "He's family."

Max stopped suddenly near the base of an old oak tree, nose buried in the dirt. He barked once, then began digging furiously. Emma and Adam exchanged a curious glance and knelt beside him. Together, they brushed away leaves and soil until Adam's hand hit something hard.

It was a small wooden box, weathered by time. Inside lay a faded letter and a single golden coin. Emma carefully read the letter aloud:

"To whoever finds this: May your adventures always be golden."

Adam raised an eyebrow. "Guess Max just found us buried treasure."

But before Emma could respond, Max snatched the coin in his mouth and bolted deeper into the woods, tail wagging wildly. Emma chased after him, laughing breathlessly, while Adam shook his head. "So much for retirement money!" he called, sprinting after them.

They finally caught up with Max at the riverbank. He dropped the coin at Emma's feet and barked proudly, as if to say, See? I told you it was important. Emma hugged him tightly, her eyes sparkling. Adam, catching his breath, put his arm around both of them.

"Looks like the real treasure," he said softly, "is this little team we've got."

As the sun dipped low, painting the sky gold, Max nestled between Emma and Adam, his tail wagging in contentment. The coin might have been old and forgotten, but their adventure together felt priceless.`;

// Utility for realistic latency simulation
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const TUTORIAL_VIDEOS = [
    "https://storage.googleapis.com/movie-app-dev-bucket-veo/output2/scenes/69939f5b0430f4cb444fb332_scene_1_merged_1771902393598.mp4",
    "https://storage.googleapis.com/movie-app-dev-bucket-veo/output2/scenes/69939f5b0430f4cb444fb332_scene_2_merged.mp4",
    "https://storage.googleapis.com/movie-app-dev-bucket-veo/output2/scenes/69939f5b0430f4cb444fb332_scene_3_merged.mp4",
    "https://storage.googleapis.com/movie-app-dev-bucket-veo/output2/scenes/69939f5b0430f4cb444fb332_scene_4_merged.mp4",
    "https://storage.googleapis.com/movie-app-dev-bucket-veo/output2/scenes/69939f5b0430f4cb444fb332_scene_5_merged.mp4"
];

const TUTORIAL_MERGED_VIDEO = "https://storage.googleapis.com/movie-app-dev-bucket-veo/output2/scenes/69939f5b0430f4cb444fb332_merged.mp4";

let videoIdxContext = 0;
let generatedScenesSet = new Set();
let generatedVideosSet = new Set();

export const TutorialMockProvider = {
    // --- TEXT INTERCEPTORS ---

    generateScreenplayOutline: async (titleObj, model) => {
        console.log('[TutorialMockProvider] Intercepting generateScreenplayOutline');
        await delay(1500);
        return {
            status: 'ok',
            screenplayOutline: tutorialScreenplay.outline,
            detailJobId: 'mock_tutorial_detail_job'
        };
    },

    getScreenplayDetailJob: async (jobId) => {
        console.log(`[TutorialMockProvider] Intercepting getScreenplayDetailJob for ${jobId}`);
        await delay(1500);
        setTimeout(() => window.dispatchEvent(new CustomEvent('tutorial-step-complete')), 500);

        const cleanScreenplay = JSON.parse(JSON.stringify(tutorialScreenplay.screenplay));
        if (cleanScreenplay.scenes) {
            cleanScreenplay.scenes.forEach(scene => {
                delete scene.image;
                delete scene.video;
                if (scene.shot_matrix) {
                    scene.shot_matrix.forEach(beat => {
                        if (beat.shots) {
                            beat.shots.forEach(shot => {
                                delete shot.videoUrl;
                                delete shot.videoHistory;
                            });
                        }
                    });
                }
            });
        }

        return {
            status: 'done',
            result: { screenplay: cleanScreenplay }
        };
    },

    generateCharacterBibleJson: async (titleObj, model, outline) => {
        console.log('[TutorialMockProvider] Intercepting generateCharacterBibleJson');
        await delay(1500);
        setTimeout(() => window.dispatchEvent(new CustomEvent('tutorial-step-complete')), 500);
        return {
            status: 'ok',
            characterMap: tutorialCharacterBible.characters || tutorialCharacterBible
        };
    },

    // --- IMAGE INTERCEPTORS ---

    sceneWithRefs: async (params) => {
        console.log('[TutorialMockProvider] Intercepting sceneWithRefs (Image Gen)');
        await delay(1200);
        // Cycle through mock scenes if scene_number wasn't provided directly
        // The previous logic in image_generation.py used max(0, scene_number - 1)
        const sceneNum = params?.scene_number || 1;
        const sceneIndex = Math.max(0, sceneNum - 1) % Math.max(1, tutorialScenes.length);

        generatedScenesSet.add(sceneNum);
        if (generatedScenesSet.size >= 5) {
            setTimeout(() => window.dispatchEvent(new CustomEvent('tutorial-step-complete')), 1000);
            generatedScenesSet.clear(); // Reset for safety
        }

        return tutorialScenes[sceneIndex]?.image || '';
    },

    // --- VIDEO GENERATION INTERCEPTORS ---

    createVideoJob: async (scenePrompt, imageUrl, options, model) => {
        console.log('[TutorialMockProvider] Intercepting createVideoJob');
        await delay(500);
        // Give it a predictable mock ID
        const jobId = `mock_tutorial_video_job_${videoIdxContext++}`;

        generatedVideosSet.add(jobId);
        if (generatedVideosSet.size >= 5) {
            setTimeout(() => window.dispatchEvent(new CustomEvent('tutorial-step-complete')), 1000);
            generatedVideosSet.clear();
        }

        return jobId;
    },

    getVideoJob: async (jobId) => {
        console.log(`[TutorialMockProvider] Intercepting getVideoJob for ${jobId}`);
        await delay(500);

        // Extract the index from the predictable mock ID to return the correct video
        // Fallback to random/cycle if can't parse
        const match = jobId.match(/mock_tutorial_video_job_(\d+)/);
        const idx = match ? parseInt(match[1], 10) : 0;
        const url = TUTORIAL_VIDEOS[idx % TUTORIAL_VIDEOS.length];

        return {
            state: 'SUCCEEDED',
            url: url
        };
    },

    // --- VIDEO MERGING INTERCEPTORS ---

    submitTranscoderMerge: async (videoUrls, outputName, geometry) => {
        console.log('[TutorialMockProvider] Intercepting submitTranscoderMerge');
        await delay(800);
        setTimeout(() => window.dispatchEvent(new CustomEvent('tutorial-step-complete')), 1500);
        return {
            jobId: 'mock_tutorial_merge_job_1',
            outputUrl: TUTORIAL_MERGED_VIDEO
        };
    },

    getTranscoderJob: async (jobId) => {
        console.log(`[TutorialMockProvider] Intercepting getTranscoderJob for ${jobId}`);
        await delay(500);
        return {
            state: 'SUCCEEDED'
        };
    },

    // Additional intercepts if needed for shot level video generation (advanced feature)
    generateSceneShotVideos: async (payload) => {
        console.log('[TutorialMockProvider] Intercepting generateSceneShotVideos');
        await delay(1000);
        const shotsList = payload.shotsList || [];
        const results = shotsList.map((shot, idx) => ({
            id: shot.id,
            jobId: `mock_tutorial_shot_job_${idx}`,
            state: "SUCCEEDED",
            videoUrl: TUTORIAL_VIDEOS[idx % TUTORIAL_VIDEOS.length],
            seed: 12345
        }));
        return { status: "ok", results };
    }
};
